// components
import dayjs from "dayjs";
import { DatePicker, Form, Input, Select, Radio } from "antd";
import { Head } from "components/molecules/Head/Head";
import { useOfferIssuancePage } from "containers/OfferIssuance/useOfferIssuancePage";
import Label from "components/atoms/Label/Label";
import Button from "components/atoms/Button/Button";
import Spinner from "components/atoms/Spinner/Spinner";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import getIcon from "utils/getIcon";
import clsx from "clsx";

// styles
import styles from "containers/OfferIssuance/offerIssuance.module.css";

// constants
import { PageTitles, DateTimeFormats } from "constants/global";

const OfferIssuancePage = () => {
	const {
		member,
		memberChange,
		cardNumber,
		cardNumberStart,
		cardNumberEnd,
		quantity,
		errorStat,
		handleEndDate,
		handleStartDate,
		handleCardNumber,
		handleCardNumberStart,
		handleCardNumberEnd,
		handleQuantity,
		handleRemarks,
		offer,
		handleOffer,
		offerSelect,
		form,
		OfferIssuanceSubmit,
		errorStartDate,
		errorEndDate,
		errorQuantity,
		errorCardNumber,
		remarks,
		loading,
		startDate,
		endDate,
		errorMessageQuantity,
		errorMessageCardNumber,
		errorMessageCardStart,
		errorMessageCardEnd,
		errorCardStart,
		errorCardEnd,
		errorOffer,
		handleClearAll
	} = useOfferIssuancePage();

	return (
		<div className="min-h-screen relative">
			{loading && <Spinner type="transparent" />}
			<PageTitle title="Offer Issuance" subTitle="Issue offer for provided card numbers" />
			<div className={styles?.root}>
				<Head>{PageTitles.OFFER_ISSUANCE}</Head>

				<Form form={form} requiredMark={false} layout="vertical">
					<div className=" grid grid-cols-2 mt-6">
						<Form.Item name="Member" initialValue={member}>
							<Radio.Group onChange={memberChange} value={member}>
								<Radio value={true}>Member</Radio>
								<Radio value={false}>Non Member</Radio>
							</Radio.Group>
						</Form.Item>
					</div>
					{member ? (
						<div className=" grid grid-cols-2 mt-6">
							<Form.Item
								label={<Label requiredMark label="CR Card Numbers" classes={{ label: styles?.basic_input_label }} />}
								name="cardNumber"
								extra={errorCardNumber && member && <ErrorMessage classes={styles?.err_message} message={errorMessageCardNumber as string} />}
							>
								<Input.TextArea
									value={cardNumber}
									data-testid="0"
									placeholder="Provide single card number or multiple card numbers seperated by comma"
									onChange={(e) => handleCardNumber(e.target.value as any)}
									rows={4}
									style={{ width: "100%", borderRadius: "0.5rem", border: "2px solid #dfe1e6" }}
								/>
							</Form.Item>
						</div>
					) : (
						<div className=" grid grid-cols-2">
							<div className=" grid grid-cols-2 mt-6">
								<Form.Item
									className="mb-auto mr-6"
									label={<Label requiredMark label="CR Card Number Range Start" classes={{ label: styles?.basic_input_label }} />}
									name="cardNumberStart"
									extra={errorCardStart && !member && <ErrorMessage classes={styles?.err_message} message={errorMessageCardStart as string} />}
								>
									<Input
										className={clsx(styles?.input_field, { [styles?.filled]: cardNumberStart })}
										placeholder=""
										value={cardNumberStart}
										style={{ width: "100%" }}
										onChange={(e) => handleCardNumberStart(e.target.value as any)}
									/>
								</Form.Item>
								<Form.Item
									className="mb-auto"
									label={<Label requiredMark label="CR Card Number Range End" classes={{ label: styles?.basic_input_label }} />}
									name="cardNumberEnd"
									extra={errorCardEnd && !member && <ErrorMessage classes={styles?.err_message} message={errorMessageCardEnd as string} />}
								>
									<Input
										className={clsx(styles?.input_field, { [styles?.filled]: cardNumberEnd })}
										style={{ width: "100%" }}
										placeholder=""
										value={cardNumberEnd}
										onChange={(e) => handleCardNumberEnd(e.target.value as any)}
									/>
								</Form.Item>
							</div>
						</div>
					)}
					<div className=" grid grid-cols-2 mt-6">
						<Form.Item
							name="voucher"
							extra={errorOffer && !offer && <ErrorMessage classes={styles?.err_message} message="This field is required" />}
							label={<Label label="Offer" classes={{ label: styles?.basic_input_label }} requiredMark />}
						>
							<Select
								className={offer ? styles?.active_select_input : styles?.selectInput}
								value={offer}
								bordered={false}
								style={{ textAlign: "center" }}
								onChange={(e) => handleOffer(e as any)}
								suffixIcon={getIcon({ icon: "caretDropDown" })}
								options={offerSelect}
								showSearch
								optionFilterProp="label"
								filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
							></Select>
						</Form.Item>
					</div>
					<div className=" grid grid-cols-2 mt-6">
						<Form.Item
							label={<Label label="Quantity" classes={{ label: styles?.basic_input_label }} requiredMark />}
							name="quantity"
							extra={errorQuantity && (!quantity || quantity <= 0 || quantity > 200) && <ErrorMessage classes={styles?.err_message} message={errorMessageQuantity as string} />}
						>
							<Input
								type="number"
								className={`${styles?.input_field}  ${quantity || ""}`}
								placeholder=""
								value={quantity}
								onChange={(e) => handleQuantity(e.target.value as any)}
							/>
						</Form.Item>
					</div>
					<div className=" grid grid-cols-2 mt-6">
						<Form.Item
							label={<Label label="Start Date" classes={{ label: styles?.basic_input_label }} />}
							name="startDate"
							extra={errorStartDate && !startDate && <ErrorMessage classes={styles?.err_message} message="This field should not be empty" />}
						>
							<DatePicker
								data-testid="Start Date"
								allowClear={false}
								className={`${styles?.date_picker}`}
								style={{ width: "100%" }}
								placeholder="Start date"
								onChange={(_, dateString) => {
									handleStartDate(dateString);
								}}
								suffixIcon={getIcon({ icon: "calender" })}
								format={DateTimeFormats.USE_YYYY_MM_DD}
							/>
						</Form.Item>
					</div>
					<div className=" grid grid-cols-2 mt-6">
						<Form.Item
							extra={errorEndDate && !endDate && <ErrorMessage classes={styles?.err_message} message="This field should not be empty" />}
							label={<Label label="End Date" classes={{ label: styles?.basic_input_label }} />}
							name="endDate"
						>
							<DatePicker
								data-testid="End Date"
								style={{ width: "100%" }}
								allowClear={false}
								className={`${styles?.date_picker}`}
								placeholder="End Date"
								onChange={(_, dateString) => {
									handleEndDate(dateString);
								}}
								suffixIcon={getIcon({ icon: "calender" })}
								format={DateTimeFormats.USE_YYYY_MM_DD}
								disabledDate={(current) => {
									return current && dayjs(current as any).isBefore(dayjs(startDate), "day");
								}}
							/>
						</Form.Item>
					</div>
					<div className=" grid grid-cols-2 mt-6">
						<Form.Item
							label={<Label requiredMark label="Remarks" classes={{ label: styles?.basic_input_label }} />}
							name="Remarks"
							extra={errorStat && !remarks && <ErrorMessage classes={styles?.err_message} message="This field is required" />}
						>
							<Input.TextArea
								value={remarks}
								placeholder=""
								onChange={(e) => handleRemarks(e.target.value as any)}
								rows={4}
								style={{ width: "100%", borderRadius: "0.5rem", border: "2px solid #dfe1e6" }}
							/>
						</Form.Item>
					</div>
					<div className={` grid grid-cols-2 place-items-end`}>
						<div className="col-span-1 flex justify-end space-x-4 mt-2">
							<Button
								label="Clear all"
								background="yellow"
								onClick={() => {
									handleClearAll("All Fields has been cleared");
								}}
							/>

							<Button label="Submit" onClick={OfferIssuanceSubmit} />
						</div>
					</div>
				</Form>
			</div>
		</div>
	);
};

export default OfferIssuancePage;
